import { useState } from 'react';
import { auth } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ChangeEmail } from './ChangeEmail';

/**
 * Shown when a checkout is refused with code `email_unverified` — the one and
 * only place Riffloop asks anyone to confirm their address.
 *
 * Why here and nowhere else: signing up, signing in and the whole free tier are
 * deliberately unblocked, so nothing stands between a new user and hearing the
 * product work. A purchase is different — it ties a real payment, a licence and
 * every future receipt to that address, and the billing IPN matches orders back
 * by email, so it has to be an inbox they can actually reach.
 *
 * Only ever reachable by a brand-new email+password signup: Google accounts are
 * stamped verified at sign-in, and every account predating this was backfilled.
 */
export function VerifyEmailNotice({ detail }: { detail?: string }) {
  // The address comes from the session rather than the error body: request()
  // in lib/api.ts only lifts `detail` and `code` off a failed response, so
  // anything else the endpoint sends never reaches the caller.
  const { user } = useAuth();
  const email = user?.email;
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  async function resend() {
    if (state === 'sending') return;
    setState('sending');
    try {
      await auth.sendVerification();
      setState('sent');
    } catch {
      // Includes the per-account rate limit. Don't dress a refusal up as a send.
      setState('failed');
    }
  }

  return (
    <div
      role="alert"
      style={{
        border: '1px solid var(--warning-amber)',
        background: 'var(--panel-2)',
        borderRadius: 10,
        padding: '14px 16px',
        maxWidth: 420,
        textAlign: 'left',
        display: 'grid',
        gap: 10,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14 }}>Confirm your email to upgrade</div>
      <div style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.55 }}>
        {detail ||
          'We sent a confirmation link when you signed up. Open it, then come back and upgrade.'}
        {email && (
          <>
            {' '}
            It went to <strong style={{ color: 'var(--text)' }}>{email}</strong>.
          </>
        )}
      </div>
      {state === 'sent' ? (
        <div style={{ fontSize: 13, color: 'var(--success-green)' }}>
          Sent — check your inbox (and your spam folder).
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            className="rl-btn-secondary"
            onClick={resend}
            disabled={state === 'sending'}
            style={{ padding: '8px 14px', fontSize: 13 }}
          >
            {state === 'sending' ? 'Sending…' : 'Send a new link'}
          </button>
          {state === 'failed' && (
            <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
              Couldn't send just now — try again in a few minutes.
            </span>
          )}
        </div>
      )}
      {/* The second (and last) chance to catch a typo — by this point they've
          hit the wall, so the address being wrong is the likeliest reason the
          link never arrived. */}
      <ChangeEmail onChanged={() => setState('sent')} />
    </div>
  );
}
